/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { analyzeIsJsonSubSchema } from "@google-labs/breadboard-schema/subschema.js";
import { DEFAULT_SCHEMA, EdgeType } from "./schemas.js";
import { PortStatus, } from "../types.js";
export { describerResultToPorts };
const title = (schema, key) => {
    return schema.properties?.[key]?.title || key;
};
export const computePortStatus = (wired, expected, required, wiredContainsStar) => {
    if (wired) {
        if (expected)
            return PortStatus.Connected;
        return wiredContainsStar ? PortStatus.Indeterminate : PortStatus.Dangling;
    }
    if (required) {
        return wiredContainsStar ? PortStatus.Indeterminate : PortStatus.Missing;
    }
    return PortStatus.Ready;
};
/**
 * A mapping from each Breadboard behavior to whether or not that behavior
 * matters for type-checking.
 */
const BEHAVIOR_AFFECTS_TYPE_CHECKING = {
    deprecated: false,
    transient: false,
    config: false,
    "google-drive-query": false,
    "google-drive-file-id": false,
    // TODO(aomarks) Not sure about many of these. Some affect the data type, some
    // only affect formatting?
    bubble: true,
    board: true,
    stream: true,
    error: true,
    "llm-content": true,
    "json-schema": true,
    "ports-spec": true,
    "hint-text": false,
    "hint-image": false,
    "hint-audio": false,
    "hint-code": false,
    "hint-multimodal": false,
    "hint-preview": false,
    "hint-single-line": false,
    module: true,
    side: false,
    "main-port": false,
    "at-wireable": false,
};
export const collectPorts = (type, edges, schema, addErrorPort, allowRequired, values) => {
    let wiredContainsStar = false;
    // Get the list of all ports wired to this node.
    const wiredPortNames = edges.map((edge) => {
        if (edge.out === "*") {
            wiredContainsStar = true;
            return "*";
        }
        return type === EdgeType.In ? edge.in : edge.out;
    });
    schema ??= {};
    const fixed = schema.additionalProperties === false;
    const schemaPortNames = Object.keys(schema.properties || {});
    if (addErrorPort) {
        // Even if not specified in the schema, all non-built-in nodes always have
        // an optional `$error` port.
        schemaPortNames.push("$error");
    }
    const schemaContainsStar = schemaPortNames.includes("*");
    const requiredPortNames = schema.required || [];
    const valuePortNames = Object.keys(values || {});
    const portNames = [
        ...new Set([
            ...wiredPortNames,
            ...schemaPortNames,
            ...valuePortNames,
            "*", // Always include the star port.
            "", // Always include the control port.
        ]),
    ];
    portNames.sort();
    return portNames
        .map((port) => {
        const star = port === "*" || port === "";
        const configured = valuePortNames.includes(port);
        const wired = wiredPortNames.includes(port);
        const expected = schemaPortNames.includes(port) || star;
        const required = requiredPortNames.includes(port);
        const portSchema = schema.properties?.[port] || DEFAULT_SCHEMA;
        const kind = computeKind(type, portSchema);
        if (portSchema.behavior?.includes("deprecated") && !wired)
            return null;
        return {
            name: port,
            title: title(schema, port),
            configured,
            value: values?.[port],
            star,
            get edges() {
                if (!wired)
                    return [];
                return edges.filter((edge) => {
                    if (edge.out === "*" && star)
                        return true;
                    return type === EdgeType.In ? edge.in === port : edge.out === port;
                });
            },
            status: computePortStatus(wired || configured, !fixed || expected || schemaContainsStar, allowRequired && required, wiredContainsStar),
            schema: portSchema,
            type: new PortType(portSchema),
            kind,
        };
    })
        .filter(Boolean);
};
function computeKind(type, portSchema) {
    const behaviors = (portSchema.type === "array"
        ? behaviorFromArray(portSchema.items)
        : portSchema.behavior) || [];
    if (type === EdgeType.Out)
        return "output";
    const match = new Set(["config", "board", "side"]);
    let count = 0;
    behaviors.forEach((behavior) => {
        if (match.has(behavior))
            ++count;
    });
    if (count == 3)
        return "side";
    return "input";
}
function behaviorFromArray(items) {
    if (!items)
        return [];
    if (Array.isArray(items)) {
        return items.flatMap((item) => item.behavior || []) || [];
    }
    return items.behavior || [];
}
export class PortType {
    schema;
    constructor(schema) {
        this.schema = schema;
    }
    hasBehavior(behavior) {
        return !!this.schema.behavior?.includes(behavior);
    }
    canConnect(to) {
        return this.analyzeCanConnect(to).canConnect;
    }
    analyzeCanConnect(to) {
        // Check standard JSON Schema subset rules.
        const subSchemaAnalysis = analyzeIsJsonSubSchema(this.schema, to.schema);
        if (!subSchemaAnalysis.isSubSchema) {
            return {
                canConnect: false,
                details: subSchemaAnalysis.details.map((detail) => ({
                    message: "Incompatible schema",
                    detail: {
                        outputPath: detail.pathA,
                        inputPath: detail.pathB,
                    },
                })),
            };
        }
        // Check Breadboard-specific behaviors.
        const fromBehaviors = new Set(this.schema.behavior);
        for (const toBehavior of to.schema.behavior ?? []) {
            if (BEHAVIOR_AFFECTS_TYPE_CHECKING[toBehavior] &&
                !fromBehaviors.has(toBehavior)) {
                return {
                    canConnect: false,
                    details: [
                        {
                            message: "Incompatible behaviors",
                            detail: { outputPath: ["behavior"], inputPath: ["behavior"] },
                        },
                    ],
                };
            }
        }
        return { canConnect: true };
    }
}
export const collectPortsForType = (schema, kind) => {
    const portNames = Object.keys(schema.properties || {});
    const requiredPortNames = schema.required || [];
    portNames.sort();
    return portNames.map((port) => {
        const portSchema = schema.properties?.[port] || DEFAULT_SCHEMA;
        return {
            name: port,
            title: title(schema, port),
            configured: false,
            star: false,
            edges: [],
            value: null,
            status: computePortStatus(false, true, requiredPortNames.includes(port), false),
            schema: portSchema,
            type: new PortType(portSchema),
            kind,
        };
    });
};
/**
 * CAUTION: Side-effectey. Will remove side-wire ports from `inputs`.
 */
export function filterSidePorts(inputs) {
    const sidePorts = [];
    const inputPorts = inputs.ports.filter((port) => {
        if (port.kind === "side") {
            sidePorts.push(port);
            return false;
        }
        return true;
    });
    inputs.ports = inputPorts;
    return sidePorts;
}
function describerResultToPorts(node, described, updating, inputValues, outputValues) {
    const incoming = node.incoming();
    const outgoing = node.outgoing();
    const inputs = {
        fixed: described.inputSchema?.additionalProperties === false,
        ports: collectPorts(EdgeType.In, incoming, described.inputSchema, false, true, { ...node.configuration(), ...inputValues }),
    };
    const side = {
        fixed: true,
        ports: filterSidePorts(inputs),
    };
    const addErrorPort = node.descriptor.type !== "input" && node.descriptor.type !== "output";
    const outputs = {
        fixed: described.outputSchema?.additionalProperties === false,
        ports: collectPorts(EdgeType.Out, outgoing, described.outputSchema, addErrorPort, false, outputValues),
    };
    return { inputs, outputs, side, updating };
}
//# sourceMappingURL=ports.js.map